import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { PasswordRequirementsTooltip } from "@/modules/shared/ui/components/password-requirements-tooltip"
import { useDispatch } from "react-redux"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { toast } from "sonner"
import { Loader2, CheckCircle2, XCircle, Building2 } from "lucide-react"
import {
  getInviteInfo,
  acceptInvite,
  rejectInvite,
  InviteInfo,
} from "../../infrastructure/organization-service"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { apiClient } from "@/lib/api-client"
import { AppDispatch } from "@/store"
import { restoreAuth } from "@/modules/auth/store/authSlice"
import { broadcastLogin } from "@/lib/session-sync"
import { AuthUser } from "@/modules/auth/entities/AuthUser"

interface LegalDocsInfo {
  terms?: { version: string }
  privacy?: { version: string }
}

export default function OrganizationInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated, loginWithCredentials, fetchUserProfile } = useAuth()
  const { setOrganizationById } = useOrganizationsService()
  const { getToken } = useRecaptcha()

  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<"accepted" | "rejected" | null>(null)

  const [email, setEmail] = useState("")
  const [tempPassword, setTempPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [password, setPassword] = useState("")

  const [errors, setErrors] = useState<{
    email?: string
    tempPassword?: string
    newPassword?: string
    confirmPassword?: string
    password?: string
    consent?: string
  }>({})

  const [showTempPassword, setShowTempPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const [legalDocs, setLegalDocs] = useState<LegalDocsInfo | null>(null)
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [agreedPrivacy, setAgreedPrivacy] = useState(false)

  useEffect(() => {
    if (!token) {
      setError("Invalid invite token")
      setLoading(false)
      return
    }

    async function fetchInvite() {
      try {
        const info = await getInviteInfo(token!)
        setInvite(info)
        if (info.email) {
          setEmail(info.email)
        }
        if (info.isNewUser) {
          try {
            const docsRes =
              await apiClient.get<LegalDocsInfo>("/legal-documents")
            setLegalDocs(docsRes.data)
          } catch {
            toast.error("Failed to load legal documents. Please try again.")
          }
        }
      } catch (err: unknown) {
        setError((err as Error).message || "Failed to load invite information")
      } finally {
        setLoading(false)
      }
    }

    fetchInvite()
  }, [token])

  const validateForm = (): boolean => {
    const newErrors: {
      email?: string
      tempPassword?: string
      newPassword?: string
      confirmPassword?: string
      password?: string
      consent?: string
    } = {}

    if (!email) {
      newErrors.email = "Email is required"
    }
    if (invite?.isNewUser) {
      if (!tempPassword)
        newErrors.tempPassword = "Temporary password is required"
      if (!newPassword) newErrors.newPassword = "New password is required"
      if (!confirmPassword)
        newErrors.confirmPassword = "Confirm password is required"

      if (!agreedTerms || !agreedPrivacy) {
        newErrors.consent =
          "You must agree to the Terms & Conditions and Privacy Policy"
      }

      if (newPassword && newPassword.length < 8) {
        newErrors.newPassword = "Password must be at least 8 characters long"
      }

      const uppercaseRegex = /[A-Z]/
      const lowercaseRegex = /[a-z]/
      const numberRegex = /[0-9]/
      const specialRegex = /[!@#$%^&*]/

      if (
        newPassword &&
        (!uppercaseRegex.test(newPassword) ||
          !lowercaseRegex.test(newPassword) ||
          !numberRegex.test(newPassword) ||
          !specialRegex.test(newPassword))
      ) {
        newErrors.newPassword =
          "Password must contain uppercase, lowercase, number, and special character (!@#$%^&*)"
      }

      if (newPassword && confirmPassword && newPassword !== confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match"
      }

      if (tempPassword && newPassword && tempPassword === newPassword) {
        newErrors.newPassword =
          "New password must be different from temporary password"
      }
    } else {
      if (!isAuthenticated && !password) {
        newErrors.password = "Password is required"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAccept = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!token) return

    if (!validateForm()) return

    setProcessing(true)
    try {
      const acceptResult = await acceptInvite(token, {
        email,
        tempPassword: invite?.isNewUser ? tempPassword : undefined,
        newPassword: invite?.isNewUser ? newPassword : undefined,
        acceptedTermsVersion: invite?.isNewUser
          ? legalDocs?.terms?.version
          : undefined,
        acceptedPrivacyVersion: invite?.isNewUser
          ? legalDocs?.privacy?.version
          : undefined,
      })

      toast.success("Invitation accepted successfully!")

      if (invite?.isNewUser) {
        if (!acceptResult) {
          toast.error("Failed to automatically log in. Please log in manually.")
          navigate("/login")
          return
        }

        const authUser: AuthUser = {
          user: {
            id: acceptResult.user.id,
            firstName: acceptResult.user.firstName,
            lastName: acceptResult.user.lastName,
            email: acceptResult.user.email,
            status: acceptResult.user.status,
            createdAt: acceptResult.user.createdAt,
            lastLoginAt: acceptResult.user.lastLoginAt,
            needsLegalConsent: acceptResult.user.needsLegalConsent,
            organizationRole: acceptResult.user.organizationRole,
            organizationRoles: acceptResult.user.organizationRoles,
          },
          roles: acceptResult.roles,
          permissions: acceptResult.permissions,
          currentOrganizationId:
            acceptResult.user.currentOrganizationId || null,
        }

        dispatch(restoreAuth({ user: authUser, loginTimestamp: Date.now() }))
        broadcastLogin(email)

        if (authUser.currentOrganizationId) {
          setOrganizationById(authUser.currentOrganizationId)
        }
        if (authUser.user.needsLegalConsent) {
          navigate("/accept-legal-documents", { replace: true })
          return
        }
        navigate("/dashboard", { replace: true })
        return
      } else {
        if (!isAuthenticated) {
          try {
            const captchaToken = await getToken("login")
            const loginResult = await loginWithCredentials(
              email,
              password,
              undefined,
              captchaToken,
            )
            if (loginResult?.user?.currentOrganizationId) {
              setOrganizationById(loginResult.user.currentOrganizationId)
            }
            if (loginResult?.user?.user?.needsLegalConsent) {
              navigate("/accept-legal-documents", { replace: true })
              return
            }
          } catch {
            toast.error(
              "Failed to automatically log in. Please log in manually.",
            )
            navigate("/login")
            return
          }
        } else {
          try {
            await fetchUserProfile()
          } catch {
            toast.error("Failed to load user profile")
            navigate("/login")
            return
          }
        }
        setResult("accepted")
      }
    } catch (err: unknown) {
      toast.error((err as Error).message || "Failed to accept invitation")
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = async () => {
    if (!token) return
    setProcessing(true)
    try {
      await rejectInvite(token)
      setResult("rejected")
      toast.success("Invitation declined")
    } catch (err: unknown) {
      toast.error((err as Error).message || "Failed to decline invitation")
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !invite) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md border-destructive">
          <CardHeader>
            <div className="flex justify-center mb-4 text-destructive">
              <XCircle className="w-12 h-12" />
            </div>
            <CardTitle className="text-center">Invite Error</CardTitle>
            <CardDescription className="text-center">
              {error || "We couldn't find this invitation."}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate("/login")}>Go to Login</Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (result === "accepted") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-slate-50">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <div className="flex justify-center mb-4 text-green-500">
              <CheckCircle2 className="w-12 h-12" />
            </div>
            <CardTitle className="text-center">Welcome Aboard!</CardTitle>
            <CardDescription className="text-center">
              You are now a member of <strong>{invite.organizationName}</strong>{" "}
              and your account is set up.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button className="w-full" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (result === "rejected") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-slate-50">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <div className="flex justify-center mb-4 text-slate-400">
              <XCircle className="w-12 h-12" />
            </div>
            <CardTitle className="text-center">Invitation Declined</CardTitle>
            <CardDescription className="text-center">
              You have declined the invitation to join{" "}
              <strong>{invite.organizationName}</strong>.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Back to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-slate-50">
      <Card className="w-full max-w-md shadow-xl border-t-4 border-t-primary">
        <CardHeader>
          <div className="flex justify-center mb-4 text-primary bg-primary/10 w-16 h-16 rounded-full items-center mx-auto">
            <Building2 className="w-8 h-8" />
          </div>
          <CardTitle className="text-center text-2xl font-bold">
            Join Organization
          </CardTitle>
          <CardDescription className="text-center text-lg mt-2">
            You've been invited to join{" "}
            <span className="font-semibold text-foreground">
              {invite.organizationName}
            </span>{" "}
            on Tempered Talent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {invite.isNewUser ? (
            <p className="text-center text-sm text-muted-foreground leading-relaxed">
              Please enter the email address the invitation was sent to, the
              temporary password from your email, and choose a new password.
            </p>
          ) : (
            <p className="text-center text-sm text-muted-foreground leading-relaxed">
              {isAuthenticated
                ? "Please click accept to join the organization."
                : "Please enter your password to accept the invitation and log in."}
            </p>
          )}

          <form onSubmit={handleAccept} className="space-y-4 mt-6" noValidate>
            {!invite.isNewUser && !isAuthenticated && (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`pr-10 ${errors.password ? "border-red-500" : ""}`}
                    disabled={processing}
                  />
                  {errors.password && (
                    <p className="text-sm text-red-500">{errors.password}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-muted-foreground"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
            )}
            {invite.isNewUser && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tempPassword">Temporary Password</Label>
                  <div className="relative">
                    <Input
                      id="tempPassword"
                      type={showTempPassword ? "text" : "password"}
                      placeholder="Enter temporary password"
                      value={tempPassword}
                      onChange={(e) => setTempPassword(e.target.value)}
                      className={`pr-10 ${errors.tempPassword ? "border-red-500" : ""}`}
                      disabled={processing}
                    />
                    {errors.tempPassword && (
                      <p className="text-sm text-red-500">
                        {errors.tempPassword}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowTempPassword((prev) => !prev)}
                      className="absolute right-3 top-3 text-muted-foreground hover:text-muted-foreground"
                    >
                      {showTempPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="newPassword"
                    className="flex items-center gap-1.5"
                  >
                    <span>New Password</span>
                    <PasswordRequirementsTooltip />
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`pr-10 ${errors.newPassword ? "border-red-500" : ""}`}
                      disabled={processing}
                    />
                    {errors.newPassword && (
                      <p className="text-sm text-red-500">
                        {errors.newPassword}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      className="absolute right-3 top-3 text-muted-foreground hover:text-muted-foreground"
                    >
                      {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`pr-10 ${errors.confirmPassword ? "border-red-500" : ""}`}
                      disabled={processing}
                    />
                    {errors.confirmPassword && (
                      <p className="text-sm text-red-500">
                        {errors.confirmPassword}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute right-3 top-3 text-muted-foreground hover:text-muted-foreground"
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3 mt-2">
                  <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-md">
                    <input
                      type="checkbox"
                      id="agreedTerms"
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      disabled={processing}
                      className="w-5 h-5 mt-0.5 cursor-pointer accent-[#1A237E]"
                    />
                    <label
                      htmlFor="agreedTerms"
                      className="text-sm font-medium leading-snug cursor-pointer text-foreground"
                    >
                      I have read and agree to the latest{" "}
                      <a
                        href="/legal/terms"
                        target="_blank"
                        className="text-[#40C4FF] hover:underline font-bold whitespace-nowrap"
                      >
                        Terms &amp; Conditions
                      </a>
                    </label>
                  </div>
                  <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-md">
                    <input
                      type="checkbox"
                      id="agreedPrivacy"
                      checked={agreedPrivacy}
                      onChange={(e) => setAgreedPrivacy(e.target.checked)}
                      disabled={processing}
                      className="w-5 h-5 mt-0.5 cursor-pointer accent-[#1A237E]"
                    />
                    <label
                      htmlFor="agreedPrivacy"
                      className="text-sm font-medium leading-snug cursor-pointer text-foreground"
                    >
                      I have read and agree to the latest{" "}
                      <a
                        href="/legal/privacy"
                        target="_blank"
                        className="text-[#40C4FF] hover:underline font-bold whitespace-nowrap"
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                  {errors.consent && (
                    <p className="text-sm text-red-500">{errors.consent}</p>
                  )}
                </div>
              </>
            )}

            <div className="flex flex-col gap-3 pt-4">
              <Button
                type="submit"
                className="w-full h-11 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white"
                disabled={processing}
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Accept Invitation
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-slate-500 hover:text-red-600 hover:bg-red-50"
                onClick={handleReject}
                disabled={processing}
              >
                Decline
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
